import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import QRCodeGeneratorService from './QRCodeGenerator.service';
import './QRCodeGenerator.css';

function QRCodeGenerator() {
  const { userData, onLogout } = useOutletContext();
  const [lastLoginTime, setLastLoginTime] = useState(null);
  const [activeTab, setActiveTab] = useState('generate');
  const [toast, setToast] = useState({ open: false, message: '', kind: 'success' }); // kind: 'success' | 'error'
  
  // Generate QR states
  const [qrCount, setQrCount] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [generateSuccess, setGenerateSuccess] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [generatedPdfList, setGeneratedPdfList] = useState([]);
  const [generatedPdfLoading, setGeneratedPdfLoading] = useState(false);
  const [generatedPdfError, setGeneratedPdfError] = useState('');
  const [downloadingPdfFileName, setDownloadingPdfFileName] = useState(null);
  const [downloadingFetchedQrPdf, setDownloadingFetchedQrPdf] = useState(false);
  
  // Get QR states
  const [stateList, setStateList] = useState([]);
  const [selectedStateId, setSelectedStateId] = useState('');
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companySites, setCompanySites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [getFilterMode, setGetFilterMode] = useState('qrCode'); // 'qrCode' | 'companySite' | 'requestId'
  const [qrCodeValue, setQrCodeValue] = useState('');
  const [requestIdValue, setRequestIdValue] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [fetchedQRCode, setFetchedQRCode] = useState(null);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [qrListForCompanySite, setQrListForCompanySite] = useState([]);
  const [didRunGetSearch, setDidRunGetSearch] = useState(false);

  const { userDetails, dashboardList } = userData || {};
  const navigate = useNavigate();

  // Get roleId (handle both string and number from API)
  const userRoleId = userDetails?.roleId || userDetails?.role?.roleId || userData?.roleId || userData?.role?.roleId;
  const roleId = userRoleId != null && userRoleId !== undefined ? Number(userRoleId) : null;
  
  // Check if user is ADMIN (4) or SUPERADMIN (5)
  const canGenerateQR = Number(roleId) === 4 || Number(roleId) === 5;

  useEffect(() => {
    // Get last login time from localStorage
    const stored = localStorage.getItem('lastLoginTime');
    if (stored) {
      setLastLoginTime(new Date(stored).toLocaleString());
    } else {
      const now = new Date();
      localStorage.setItem('lastLoginTime', now.toISOString());
      setLastLoginTime(now.toLocaleString());
    }

  }, []);

  useEffect(() => {
    if (!toast.open) return undefined;
    const t = setTimeout(() => setToast((p) => ({ ...p, open: false })), 3000);
    return () => clearTimeout(t);
  }, [toast.open]);

  // Load company sites when company is selected
  useEffect(() => {
    // Only load Company/Site data when user is in:
    // Get QR Code tab + Company & Sites mode
    if (activeTab !== 'get' || getFilterMode !== 'companySite') return;
    if (selectedCompany) {
      loadCompanySites(selectedCompany);
    } else {
      setCompanySites([]);
      setSelectedSite('');
    }
  }, [activeTab, getFilterMode, selectedCompany]);

  // Reset search when switching tabs
  useEffect(() => {
    if (activeTab === 'generate') {
      setSelectedStateId('');
      setSelectedCompany('');
      setSelectedSite('');
      setFetchedQRCode(null);
      setFetchError('');
      setGetFilterMode('qrCode');
      setQrCodeValue('');
      setRequestIdValue('');
      setQrListForCompanySite([]);
      setDidRunGetSearch(false);
      // Load latest generated PDFs when landing here
      fetchGeneratedPdfs();
    } else {
      setQrCount('');
      setGenerateError('');
      setGenerateSuccess('');
      setPdfUrl(null);
    }
  }, [activeTab]);

  const fetchGeneratedPdfs = async () => {
    setGeneratedPdfLoading(true);
    setGeneratedPdfError('');
    try {
      const resp = await QRCodeGeneratorService.getAllGeneratedPdfs();
      const items = Array.isArray(resp?.items) ? resp.items : [];
      setGeneratedPdfList(items);
      return items;
    } catch (error) {
      setGeneratedPdfList([]);
      setGeneratedPdfError(error.message || 'Failed to load generated PDFs.');
      return [];
    } finally {
      setGeneratedPdfLoading(false);
    }
  };

  const loadCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      const data = await QRCodeGeneratorService.getCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading companies:', error);
      setFetchError('Failed to load companies. Please try again.');
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const loadStates = async () => {
    setIsLoadingStates(true);
    try {
      const data = await QRCodeGeneratorService.getStateList();
      setStateList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading states:', error);
      setFetchError('Failed to load states. Please try again.');
      setStateList([]);
    } finally {
      setIsLoadingStates(false);
    }
  };

  const loadCompanySites = async (companyId) => {
    setIsLoadingSites(true);
    setSelectedSite('');
    try {
      const sites = await QRCodeGeneratorService.getCompanySites(companyId);
      setCompanySites(sites);
    } catch (error) {
      console.error('Error loading company sites:', error);
      setFetchError('Failed to load company sites. Please try again.');
      setCompanySites([]);
    } finally {
      setIsLoadingSites(false);
    }
  };

  const isPdfUrlExpired = (epochSeconds) => {
    if (!epochSeconds) return false;
    const now = Math.floor(Date.now() / 1000);
    return now >= Number(epochSeconds);
  };

  const triggerBrowserDownload = async (url, fileName) => {
    if (!url) return;
    try {
      // Force a real download without navigating away.
      // For one-time download endpoints we use authenticated fetch via service.
      const blob = await QRCodeGeneratorService.downloadOneTimePdf(url);
      const objUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = objUrl;
      a.download = fileName || 'qr-codes.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();

      // Cleanup object URL
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    } catch (e) {
      // We intentionally do NOT fall back to opening a new tab.
      // If the blob download fails, surface the error to the user.
      throw e;
    }
  };

  const saveBlobToDisk = (blob, fileName) => {
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = fileName || 'qr-codes.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  };

  const handleDownloadGeneratedPdf = async (item) => {
    const fileName = String(item?.fileName || '').trim();
    if (!fileName) return;

    setDownloadingPdfFileName(fileName);
    try {
      const blob = await QRCodeGeneratorService.downloadQrPdfFile(fileName);
      saveBlobToDisk(blob, fileName);
    } catch (e) {
      setToast({ open: true, kind: 'error', message: e?.message || 'Download failed.' });
    } finally {
      setDownloadingPdfFileName(null);
    }
  };

  const handleDownloadFetchedQrPdf = async () => {
    if (getFilterMode === 'qrCode') {
      const fileName = String(fetchedQRCode?.fileName || '').trim();
      if (!fileName) return;

      setDownloadingFetchedQrPdf(true);
      try {
        const blob = await QRCodeGeneratorService.downloadSingleQrPdf(fileName);
        saveBlobToDisk(blob, fileName);
      } catch (e) {
        setToast({ open: true, kind: 'error', message: e?.message || 'Download failed.' });
      } finally {
        setDownloadingFetchedQrPdf(false);
      }
      return;
    }

    if (getFilterMode !== 'requestId' || !fetchedQRCode?.downloadUrl) return;

    setDownloadingFetchedQrPdf(true);
    try {
      if (isPdfUrlExpired(fetchedQRCode?.expiresAt)) {
        const refreshed = await QRCodeGeneratorService.searchByRequestId(requestIdValue.trim());
        setFetchedQRCode({ ...refreshed, requestId: requestIdValue.trim() });
        await triggerBrowserDownload(refreshed.downloadUrl, refreshed.fileName);
        return;
      }
      await triggerBrowserDownload(fetchedQRCode.downloadUrl, fetchedQRCode.fileName);
    } catch (e) {
      try {
        const refreshed = await QRCodeGeneratorService.searchByRequestId(requestIdValue.trim());
        setFetchedQRCode({ ...refreshed, requestId: requestIdValue.trim() });
        if (!refreshed?.downloadUrl) throw e;
        await triggerBrowserDownload(refreshed.downloadUrl, refreshed.fileName);
      } catch (retryErr) {
        setToast({ open: true, kind: 'error', message: retryErr?.message || e?.message || 'Download failed.' });
      }
    } finally {
      setDownloadingFetchedQrPdf(false);
    }
  };

  const handleGenerateQR = async (e) => {
    e.preventDefault();
    setGenerateError('');
    setGenerateSuccess('');
    setIsGenerating(true);

    // Validate input
    const count = parseInt(qrCount, 10);
    if (isNaN(count) || count < 1 || count > 8) {
      setGenerateError('Please enter a number between 1 and 8.');
      setIsGenerating(false);
      return;
    }

    try {
      const response = await QRCodeGeneratorService.generateQRCodes(count, 'QR');
      const message = response.message || `PDF generated successfully for ${count} QR code(s).`;
      setGenerateSuccess(message);
      setPdfUrl(response.pdfUrl || null);
      setQrCount('');
      // Refresh "Generated PDFs" list so the new PDF appears immediately for download
      await fetchGeneratedPdfs();
      setToast({ open: true, message: 'PDF generated successfully.', kind: 'success' });
    } catch (error) {
      console.error('Error generating QR codes:', error);
      setGenerateError(error.message || 'Failed to generate QR codes. Please try again.');
      setToast({ open: true, message: error.message || 'Failed to generate QR codes.', kind: 'error' });
      setPdfUrl(null);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFetchQR = async (e) => {
    e.preventDefault();
    setFetchError('');
    setFetchedQRCode(null);
    setIsFetching(true);
    setDidRunGetSearch(true);

    try {
      if (getFilterMode === 'qrCode') {
        if (!qrCodeValue.trim()) throw new Error('Please enter a QR Code.');
        const meta = await QRCodeGeneratorService.getSingleQrPdf(qrCodeValue.trim());
        setFetchedQRCode(meta);
        return;
      }

      if (getFilterMode === 'requestId') {
        if (!requestIdValue.trim()) throw new Error('Please enter a Request Id.');
        const meta = await QRCodeGeneratorService.searchByRequestId(requestIdValue.trim());
        setFetchedQRCode({ ...meta, requestId: requestIdValue.trim() });
        return;
      }

      if (!selectedCompany) throw new Error('Please select a company.');
      if (!selectedSite) throw new Error('Please select a company site.');
      const resp = await QRCodeGeneratorService.searchByCompanySite(selectedCompany, selectedSite, 25);
      setQrListForCompanySite(Array.isArray(resp?.items) ? resp.items : []);
    } catch (error) {
      console.error('Error fetching QR code:', error);
      setFetchError(error.message || 'Failed to fetch QR code. Please try again.');
      setFetchedQRCode(null);
      setQrListForCompanySite([]);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div className="qr-code-generator-page">
      <div className="qr-code-generator-content">
        <div className="main-content-wrapper">
          {/* Tabs */}
          <div className="qr-tabs-container">
            <div className="qr-tabs">
              <button
                type="button"
                className={`qr-tab ${activeTab === 'generate' ? 'active' : ''}`}
                onClick={() => setActiveTab('generate')}
              >
                Generate QR Codes
              </button>
              <button
                type="button"
                className={`qr-tab ${activeTab === 'get' ? 'active' : ''}`}
                onClick={() => setActiveTab('get')}
              >
                Get QR Code
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="qr-tab-content">
            {/* Generate QR Codes Tab */}
            {activeTab === 'generate' && (
              <div className="qr-tab-panel">
                {!canGenerateQR ? (
                  <div className="qr-access-denied">
                    <div className="qr-access-denied-icon">🔒</div>
                    <h3>Access restricted</h3>
                    <p>Only ADMIN and SUPERADMIN roles can generate QR codes.</p>
                  </div>
                ) : (
                  <>
                    <div className="qr-generate-grid">
                      <div className="qr-form-card">
                        <div className="qr-form-card-header">
                          <div className="qr-form-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                            </svg>
                          </div>
                          <h2>Generate QR Codes</h2>
                          <p className="qr-form-subtitle">Enter a number between 1 and 8 to generate QR codes in bulk.</p>
                        </div>
                        {generateError && (
                          <div style={{ marginBottom: 12 }}>
                            <div className="error-message">
                              <span className="message-icon">✕</span>
                              <span>{generateError}</span>
                            </div>
                          </div>
                        )}
                        <form className="qr-form" onSubmit={handleGenerateQR}>
                          <div className="form-group">
                            <label htmlFor="qrCount">Number of QR Codes to Generate <span className="required">*</span></label>
                            <input
                              type="number"
                              id="qrCount"
                              name="qrCount"
                              min="1"
                            max="8"
                              value={qrCount}
                              onChange={(e) => {
                                const value = e.target.value;
                              if (value === '' || (parseInt(value, 10) >= 1 && parseInt(value, 10) <= 8)) {
                                  setQrCount(value);
                                  setGenerateError('');
                                }
                              }}
                              required
                            placeholder="Enter number (1-8)"
                              disabled={isGenerating}
                            />
                          <span className="form-hint">Enter a number between 1 and 8</span>
                          </div>
                          <div className="form-actions">
                            <button
                              type="submit"
                              className="submit-button"
                              disabled={isGenerating || !qrCount}
                            >
                              {isGenerating ? (
                                <>
                                  <span className="button-spinner" aria-hidden></span>
                                  Generating...
                                </>
                              ) : (
                                'Generate QR Codes'
                              )}
                            </button>
                            {activeTab === 'generate' && toast.open && (
                              <div
                                className={`qr-inline-toast ${toast.kind === 'error' ? 'qr-inline-toast--error' : 'qr-inline-toast--success'}`}
                                role="status"
                                aria-live="polite"
                              >
                                {toast.message}
                              </div>
                            )}
                          </div>
                        </form>
                      </div>

                      {/* Download already generated PDFs (UI only for now) */}
                      <div className="qr-existing-pdf-card qr-existing-pdf-card--compact">
                        <div className="qr-existing-pdf-header">
                          <div>
                            <h3>Generated QR PDFs</h3>
                          </div>
                          <button
                            type="button"
                            className="qr-existing-pdf-refresh"
                            onClick={fetchGeneratedPdfs}
                            disabled={generatedPdfLoading}
                            title="Refresh"
                            aria-label="Refresh PDF list"
                          >
                            <span className="qr-existing-pdf-refresh__icon" aria-hidden>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23 4 23 10 17 10"/>
                                <polyline points="1 20 1 14 7 14"/>
                                <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10"/>
                                <path d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14"/>
                              </svg>
                            </span>
                            <span className="qr-existing-pdf-refresh__label">Refresh</span>
                          </button>
                        </div>

                        <div className="qr-existing-pdf-table-wrap">
                          {generatedPdfLoading && (
                            <div className="qr-existing-pdf-inline-note">Loading PDFs…</div>
                          )}
                          {!generatedPdfLoading && generatedPdfError && (
                            <div className="qr-existing-pdf-inline-note qr-existing-pdf-inline-note--error">{generatedPdfError}</div>
                          )}
                          {!generatedPdfLoading && !generatedPdfError && (generatedPdfList || []).length === 0 && (
                            <div className="qr-existing-pdf-inline-note">No PDFs found.</div>
                          )}
                          {!generatedPdfLoading && !generatedPdfError && (generatedPdfList || []).length > 0 && (
                            <table className="qr-existing-pdf-table">
                              <thead>
                                <tr>
                                  <th>File</th>
                                  <th>Created</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {generatedPdfList.map((row) => (
                                  <tr key={row.s3Key || row.fileName}>
                                    <td>
                                      <div className="qr-existing-pdf-file">
                                        <span className="qr-existing-pdf-file-name">{row.fileName}</span>
                                      </div>
                                    </td>
                                    <td>{row.createdDate || '—'}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="qr-existing-pdf-icon-btn"
                                        aria-label="Download PDF"
                                        title="Download"
                                        onClick={() => handleDownloadGeneratedPdf(row)}
                                        disabled={!row.fileName || downloadingPdfFileName === row.fileName}
                                        aria-busy={downloadingPdfFileName === row.fileName}
                                      >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                          <polyline points="7 10 12 15 17 10"/>
                                          <line x1="12" y1="15" x2="12" y2="3"/>
                                        </svg>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Get QR Code Tab */}
            {activeTab === 'get' && (
              <div className="qr-tab-panel">
                <div className="qr-get-grid">
                  <div className="qr-form-card qr-get-card">
                    <div className="qr-form-card-header">
                      <div className="qr-form-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
                        </svg>
                      </div>
                      <h2>Get QR Code</h2>
                      <p className="qr-form-subtitle">Find QR by code, request id, or by company &amp; site to download.</p>
                    </div>
                    <form className="qr-form" onSubmit={handleFetchQR}>
                    <div className="form-group">
                      <label>Filter By</label>
                      <div className="qr-filter-tabs" role="tablist" aria-label="Get QR Code filters">
                        <button
                          type="button"
                          className={`qr-filter-tab ${getFilterMode === 'qrCode' ? 'active' : ''}`}
                          onClick={() => {
                            setGetFilterMode('qrCode');
                            setFetchError('');
                            setFetchedQRCode(null);
                            setQrListForCompanySite([]);
                            setSelectedCompany('');
                            setSelectedSite('');
                            setDidRunGetSearch(false);
                          }}
                        >
                          QR Code
                        </button>
                        <button
                          type="button"
                          className={`qr-filter-tab ${getFilterMode === 'companySite' ? 'active' : ''}`}
                          onClick={() => {
                            setGetFilterMode('companySite');
                            setFetchError('');
                            setFetchedQRCode(null);
                            setQrListForCompanySite([]);
                            setDidRunGetSearch(false);
                            // Lazy load companies only when this mode is used
                            if (!isLoadingCompanies && (!companies || companies.length === 0)) loadCompanies();
                            if (!isLoadingStates && (!stateList || stateList.length === 0)) loadStates();
                          }}
                        >
                          Company &amp; Site
                        </button>
                        <button
                          type="button"
                          className={`qr-filter-tab ${getFilterMode === 'requestId' ? 'active' : ''}`}
                          onClick={() => {
                            setGetFilterMode('requestId');
                            setFetchError('');
                            setFetchedQRCode(null);
                            setQrListForCompanySite([]);
                            setSelectedCompany('');
                            setSelectedSite('');
                            setDidRunGetSearch(false);
                          }}
                        >
                          Request Id
                        </button>
                      </div>
                        <div className="qr-filter-hint">
                          {getFilterMode === 'qrCode' && 'Paste/type a QR code to fetch details.'}
                          {getFilterMode === 'companySite' && 'Select Company & Site to load a downloadable QR list.'}
                          {getFilterMode === 'requestId' && 'Enter a Request Id to fetch QR details.'}
                        </div>
                    </div>

                    {getFilterMode === 'qrCode' && (
                      <div className="form-group">
                        <label htmlFor="qrCodeValue">QR Code <span className="required">*</span></label>
                        <input
                          type="text"
                          id="qrCodeValue"
                          name="qrCodeValue"
                          value={qrCodeValue}
                          onChange={(e) => { setQrCodeValue(e.target.value); setFetchError(''); }}
                          required
                          placeholder="Enter QR code"
                          disabled={isFetching}
                        />
                      </div>
                    )}

                    {getFilterMode === 'requestId' && (
                      <div className="form-group">
                        <label htmlFor="requestIdValue">Request Id <span className="required">*</span></label>
                        <input
                          type="text"
                          id="requestIdValue"
                          name="requestIdValue"
                          value={requestIdValue}
                          onChange={(e) => { setRequestIdValue(e.target.value); setFetchError(''); }}
                          required
                          placeholder="Enter request id"
                          disabled={isFetching}
                        />
                      </div>
                    )}

                    {getFilterMode === 'companySite' && (
                      <>
                        <div className="form-group">
                          <label htmlFor="state">State <span className="required">*</span></label>
                          <select
                            id="state"
                            name="state"
                            value={selectedStateId}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSelectedStateId(v);
                              setSelectedCompany('');
                              setSelectedSite('');
                              setCompanySites([]);
                              setFetchError('');
                            }}
                            required
                            disabled={isLoadingStates || isFetching}
                          >
                            <option value="">Select State</option>
                            {stateList.map((s) => (
                              <option key={s.stateId} value={String(s.stateId)}>
                                {s.stateName} ({s.stateCode})
                              </option>
                            ))}
                          </select>
                          {isLoadingStates && (
                            <span className="form-hint">Loading states...</span>
                          )}
                        </div>

                        <div className="form-group">
                          <label htmlFor="company">Company <span className="required">*</span></label>
                          <select
                            id="company"
                            name="company"
                            value={selectedCompany}
                            onChange={(e) => {
                              setSelectedCompany(e.target.value);
                              setFetchError('');
                            }}
                            required
                            disabled={!selectedStateId || isLoadingCompanies || isFetching}
                          >
                            <option value="">{selectedStateId ? 'Select Company' : 'Select State first'}</option>
                            {companies
                              .filter((c) => selectedStateId && String(c.stateId) === String(selectedStateId))
                              .map((company) => (
                              <option key={company.companyId || company.id} value={company.companyId || company.id}>
                                {company.companyName || company.name}
                              </option>
                              ))}
                          </select>
                          {isLoadingCompanies && (
                            <span className="form-hint">Loading companies...</span>
                          )}
                        </div>

                        <div className="form-group">
                          <label htmlFor="site">Company Site <span className="required">*</span></label>
                          <select
                            id="site"
                            name="site"
                            value={selectedSite}
                            onChange={(e) => {
                              setSelectedSite(e.target.value);
                              setFetchError('');
                            }}
                            required
                            disabled={!selectedCompany || isLoadingSites || isFetching}
                          >
                            <option value="">Select Site</option>
                            {companySites.map((site) => (
                              <option key={site.siteId || site.id} value={site.siteId || site.id}>
                                {site.siteName || site.name}
                              </option>
                            ))}
                          </select>
                          {isLoadingSites && (
                            <span className="form-hint">Loading sites...</span>
                          )}
                          {selectedCompany && companySites.length === 0 && !isLoadingSites && (
                            <span className="form-hint form-hint-error">No sites available for this company.</span>
                          )}
                        </div>
                      </>
                    )}

                    <div className="form-actions">
                      <button
                        type="submit"
                        className="submit-button"
                        disabled={isFetching || (getFilterMode === 'qrCode' && !qrCodeValue.trim()) || (getFilterMode === 'requestId' && !requestIdValue.trim()) || (getFilterMode === 'companySite' && (!selectedCompany || !selectedSite))}
                      >
                        {isFetching ? (
                          <>
                            <span className="button-spinner" aria-hidden></span>
                            Loading...
                          </>
                        ) : (
                          getFilterMode === 'companySite' ? 'Load QR List' : 'Get QR Code'
                        )}
                      </button>
                    </div>

                    {fetchError && (
                      <div className="error-message">
                        <span className="message-icon">✕</span>
                        <span>{fetchError}</span>
                      </div>
                    )}
                    </form>
                  </div>

                  <div className="qr-get-results">
                    <div className="qr-results-card">
                      <div className="qr-results-header">
                        <h3>Results</h3>
                        <p>
                          {getFilterMode === 'companySite'
                            ? 'Download QR PDFs from the list.'
                            : 'Review the QR details below.'}
                        </p>
                      </div>
                      <div className="qr-results-body">
                    {/* List of QRs for Company & Site */}
                    {getFilterMode === 'companySite' && (
                      <>
                        {qrListForCompanySite.length > 0 ? (
                          <div className="qr-existing-pdf-table-wrap" style={{ padding: 0 }}>
                            {qrListForCompanySite.map((row) => (
                              <div className="qr-pdf-result-row" key={row.qrUniqueId || row.fileName}>
                                <div className="qr-pdf-result-row__item">
                                  <div className="qr-pdf-result-row__label">QR Code</div>
                                  <div className="qr-pdf-result-row__value">{row?.qrUniqueId || '—'}</div>
                                </div>
                                <div className="qr-pdf-result-row__item">
                                  <div className="qr-pdf-result-row__label">File</div>
                                  <div className="qr-pdf-result-row__value">{row?.fileName || '—'}</div>
                                </div>
                                <div className="qr-pdf-result-row__action">
                                  <button
                                    type="button"
                                    className="qr-existing-pdf-icon-btn"
                                    aria-label="Download PDF"
                                    title="Download"
                                    disabled={!row?.downloadUrl}
                                    onClick={async () => {
                                      try {
                                        if (!row?.downloadUrl) return;

                                        if (isPdfUrlExpired(row?.expiresAt)) {
                                          const resp = await QRCodeGeneratorService.searchByCompanySite(selectedCompany, selectedSite, 25);
                                          const items = Array.isArray(resp?.items) ? resp.items : [];
                                          setQrListForCompanySite(items);
                                          const refreshed = items.find((x) => x?.qrUniqueId && row?.qrUniqueId && x.qrUniqueId === row.qrUniqueId);
                                          await triggerBrowserDownload(refreshed?.downloadUrl || row.downloadUrl, refreshed?.fileName || row.fileName);
                                          return;
                                        }

                                        await triggerBrowserDownload(row.downloadUrl, row.fileName);
                                      } catch (e) {
                                        // Retry once after refreshing the list, so users don't get stuck on expired one-time URLs.
                                        try {
                                          const resp = await QRCodeGeneratorService.searchByCompanySite(selectedCompany, selectedSite, 25);
                                          const items = Array.isArray(resp?.items) ? resp.items : [];
                                          setQrListForCompanySite(items);
                                          const refreshed = items.find((x) => x?.qrUniqueId && row?.qrUniqueId && x.qrUniqueId === row.qrUniqueId);
                                          if (!refreshed?.downloadUrl) throw e;
                                          await triggerBrowserDownload(refreshed.downloadUrl, refreshed?.fileName || row.fileName);
                                        } catch (retryErr) {
                                          setToast({ open: true, kind: 'error', message: retryErr?.message || e?.message || 'Download failed.' });
                                        }
                                      }
                                    }}
                                  >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                      <polyline points="7 10 12 15 17 10"/>
                                      <line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="qr-results-empty">
                            <div className="qr-results-empty-icon" aria-hidden>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                            </div>
                            <div>
                              <div className="qr-results-empty-title">No items yet</div>
                              <div className="qr-results-empty-subtitle">Select Company and Site, then click <strong>Load QR List</strong>.</div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Display Fetched QR Code */}
                    {fetchedQRCode && (
                      (getFilterMode === 'qrCode' || getFilterMode === 'requestId') ? (
                        <div className="qr-pdf-result-row">
                          <div className="qr-pdf-result-row__item">
                            <div className="qr-pdf-result-row__label">QR Code</div>
                            <div className="qr-pdf-result-row__value">{fetchedQRCode?.qrUniqueId || '—'}</div>
                          </div>
                          <div className="qr-pdf-result-row__item">
                            <div className="qr-pdf-result-row__label">File</div>
                            <div className="qr-pdf-result-row__value">{fetchedQRCode?.fileName || '—'}</div>
                          </div>
                          <div className="qr-pdf-result-row__action">
                            <button
                              type="button"
                              className="qr-existing-pdf-icon-btn"
                              aria-label="Download PDF"
                              title="Download"
                              disabled={
                                downloadingFetchedQrPdf ||
                                (getFilterMode === 'qrCode'
                                  ? !fetchedQRCode?.fileName
                                  : !fetchedQRCode?.downloadUrl)
                              }
                              onClick={handleDownloadFetchedQrPdf}
                              aria-busy={downloadingFetchedQrPdf}
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="qr-result-card">
                          <div className="qr-card-accent"></div>
                          <div className="qr-result-header">
                            <h3>QR Code Details</h3>
                          </div>
                          <div className="qr-result-body">
                            <div className="qr-result-details">
                              {fetchedQRCode.qrUniqueId && (
                                <div className="qr-detail-row">
                                  <span className="qr-detail-label">QR Unique ID</span>
                                  <span className="qr-detail-value">{fetchedQRCode.qrUniqueId}</span>
                                </div>
                              )}
                              {fetchedQRCode.machineryName && (
                                <div className="qr-detail-row">
                                  <span className="qr-detail-label">Machinery Name</span>
                                  <span className="qr-detail-value">{fetchedQRCode.machineryName}</span>
                                </div>
                              )}
                              {fetchedQRCode.companyName && (
                                <div className="qr-detail-row">
                                  <span className="qr-detail-label">Company</span>
                                  <span className="qr-detail-value">{fetchedQRCode.companyName}</span>
                                </div>
                              )}
                              {fetchedQRCode.siteName && (
                                <div className="qr-detail-row">
                                  <span className="qr-detail-label">Site</span>
                                  <span className="qr-detail-value">{fetchedQRCode.siteName}</span>
                                </div>
                              )}
                              {fetchedQRCode.createdAt && (
                                <div className="qr-detail-row">
                                  <span className="qr-detail-label">Created</span>
                                  <span className="qr-detail-value">{new Date(fetchedQRCode.createdAt).toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                            <div className="qr-result-image-wrap">
                              {fetchedQRCode.imageUrl ? (
                                <>
                                  <img
                                    src={fetchedQRCode.imageUrl}
                                    alt={`QR Code ${fetchedQRCode.qrUniqueId || 'QR Code'}`}
                                    className="qr-result-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const placeholder = e.target.parentElement.querySelector('.qr-result-placeholder');
                                      if (placeholder) placeholder.style.display = 'flex';
                                    }}
                                  />
                                  <div className="qr-result-placeholder" style={{ display: 'none' }}>
                                    <span>QR Code</span>
                                    {fetchedQRCode.qrUniqueId && <small>{fetchedQRCode.qrUniqueId}</small>}
                                  </div>
                                </>
                              ) : (
                                <div className="qr-result-placeholder">
                                  <span>QR Code</span>
                                  {fetchedQRCode.qrUniqueId && <small>{fetchedQRCode.qrUniqueId}</small>}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                      {!fetchedQRCode && !(getFilterMode === 'companySite' && qrListForCompanySite.length > 0) && (
                        <div className="qr-results-empty">
                          <div className="qr-results-empty-icon" aria-hidden>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="11" cy="11" r="7"/>
                              <path d="M21 21l-3.5-3.5"/>
                            </svg>
                          </div>
                          <div>
                            <div className="qr-results-empty-title">Nothing to show</div>
                            <div className="qr-results-empty-subtitle">
                              {didRunGetSearch && getFilterMode === 'qrCode'
                                ? 'No PDF found for this QR Code.'
                                : 'Choose a filter and run the search to see results here.'}
                            </div>
                          </div>
                        </div>
                      )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default QRCodeGenerator;

